import React, { useContext, useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
} from '@mui/material';
import { OrganisationSettings } from '../../../../interfaces/delivery-company/orders';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { useSnackBar } from '../../../../contexts/snackbar';
import axios from 'axios';
import FilterChips from '../../../common/filter-chips/filter-chips';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import Grid2 from '@mui/material/Unstable_Grid2';

interface BrandingProps {
  settings: OrganisationSettings;
}

interface Placeholder {
  name: string;
  type: string;
}

const templates = [
  {
    name: 'Custom',
    template: '',
    placeholders: [],
  },
  {
    name: 'Call Center Template',
    template: '{company_phone} | {support_email}\n\n{address}',
    placeholders: [
      { name: 'company_phone', type: 'phone' },
      { name: 'support_email', type: 'email' },
      { name: 'address', type: 'text' },
    ],
  },
];

const Branding = ({ settings }: BrandingProps) => {
  const { config } = useContext(AuthContext);
  const { setSettings } = useContext(OrgSettingsContext);
  const { showMessage } = useSnackBar();

  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('Custom');
  const [fieldValues, setFieldValues] = useState<{ [key: string]: string }>({});
  const [customText, setCustomText] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setCustomText(settings.branding_message || '');
  }, [settings]);

  // Reset form when template changes
  useEffect(() => {
    if (selectedTemplate === 'Custom') {
      setFieldValues({});
      return;
    }

    const template = templates.find((t) => t.name === selectedTemplate);
    if (template) {
      const extractedValues: { [key: string]: string } = {};

      template.placeholders.forEach((ph) => {
        const regex = new RegExp(`${ph.name}:\\s*(.*)`);
        const match = settings.branding_message?.match(regex);
        if (match && match[1]) {
          extractedValues[ph.name] = match[1].trim();
        } else {
          extractedValues[ph.name] = '';
        }
      });

      setFieldValues(extractedValues);
    }
  }, [selectedTemplate, settings]);

  const handleFieldChange = (key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    let valid = true;
    const newErrors: { [key: string]: string } = {};
    if (selectedTemplate !== 'Custom') {
      const template = templates.find((t) => t.name === selectedTemplate);
      template?.placeholders.forEach((ph) => {
        const value = fieldValues[ph.name]?.trim() || '';
        if (!value) {
          newErrors[ph.name] = 'Required';
          valid = false;
        } else if (ph.type === 'email' && !/^\S+@\S+\.\S+$/.test(value)) {
          newErrors[ph.name] = 'Invalid email';
          valid = false;
        } else if (ph.type === 'phone' && !/^\+?[0-9]{7,15}$/.test(value)) {
          newErrors[ph.name] = 'Invalid phone number';
          valid = false;
        }
      });
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);
    let branding_message = '';

    if (selectedTemplate === 'Custom') {
      branding_message = customText;
    } else {
      const template = templates.find((t) => t.name === selectedTemplate);
      if (template) {
        branding_message = template.template;
        template.placeholders.forEach((ph) => {
          branding_message = branding_message.replace(`{${ph.name}}`, fieldValues[ph.name] || '');
        });
      }
    }

    try {
      await axios.post(
        `${API_ENDPOINT}/api/v1/settings`,
        { branding_message },
        config
      );
      setSettings({ ...settings, branding_message });
      showMessage('Signature saved successfully.');
      setSelectedTemplate('Custom');
      setCustomText(branding_message);
    } catch (e) {
      console.error(e);
      showMessage('Failed to save.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (



        <Box display="flex" flexDirection="column" gap={3} maxWidth={600} width="100%">
         <FilterChips
          label={''}
          options={templates.map((item) => item.name)}
          selected={[selectedTemplate]}
          onChange={(selected) => {
            if (selected.length > 0) setSelectedTemplate(selected[0]);
          }}
        />

        {selectedTemplate === 'Custom' ? (
          <TextField
            multiline
            minRows={4}
            label="Content"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            fullWidth
          />
        ) : (
          <>
            {templates
              .find((t) => t.name === selectedTemplate)
              ?.placeholders.map((ph) => (
                <TextField
                  key={ph.name}
                  type={ph.type === 'text' ? 'text' : ph.type}
                  autoComplete="off"
                  label={ph.name.replace(/_/g, ' ').toUpperCase()}
                  value={fieldValues[ph.name] || ''}
                  onChange={(e) => handleFieldChange(ph.name, e.target.value)}
                  error={Boolean(errors[ph.name])}
                  helperText={errors[ph.name]}
                  fullWidth
                />
              ))}
          </>
        )}

        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </Box>

         <Alert severity="info">
          Company signature will be included in waybills and invoices.
         
          <br />
          You are advised to include your call center phone number, support email address, company address, etc.
        </Alert>



        </Box>


        




  );
};

export default Branding;
